import React, { useState, useMemo } from "react";
import { formatMiles } from "../../../../utils/miles";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

function LiquidacionesAsociadas({
  liquidaciones = [],
  liqDetalles = {},
  proveedores = [],
  loadingDetalles,
  orden
}) {
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingXlsx, setGeneratingXlsx] = useState(false);

  const totals = useMemo(() => {
    let honorarios = 0;
    let tarifa = 0;
    for (const liq of liquidaciones) {
      const det = liqDetalles[liq.idLiquidacionClientes];
      if (det) {
        if (det.honorarios !== undefined) honorarios += Number(det.honorarios) || 0;
        if (det.tarifa !== undefined) tarifa += Number(det.tarifa) || 0;
      }
    }
    for (const p of proveedores) {
      if (p.monto !== undefined) honorarios += Number(p.monto) || 0;
    }
    return { honorarios, tarifa, count: liquidaciones.length + proveedores.length };
  }, [liquidaciones, liqDetalles, proveedores]);

  const handleDownloadPdf = () => {
    if (!liquidaciones.length && !proveedores.length) return;
    generatePdf({ liquidaciones, liqDetalles, proveedores, setGeneratingPdf, orden });
  };

  const handleDownloadXlsx = () => {
    if (!liquidaciones.length && !proveedores.length) return;
    generateXlsx({ liquidaciones, liqDetalles, proveedores, setGeneratingXlsx, orden, totals });
  };

  const nothingToExport = !liquidaciones.length && !proveedores.length;

  /* ================= Estilos ================= */
  const tableStyle = {
    borderCollapse: "collapse",
    width: "100%",
    opacity: loadingDetalles ? 0.5 : 1,
    fontSize: 14,
    tableLayout: "fixed",
    minWidth: 880,
  };

  const thStyle = {
    id: baseTh("7%"),
    paciente: baseTh("30%"),
    inicio: baseTh("10%"),
    fin: baseTh("10%"),
    factura: baseTh("11%"),
    honorarios: baseTh("11%", "right"),
    tarifa: baseTh("11%", "right"),
    estado: baseTh("10%"),
  };

  return (
    <div style={{ position: "relative" }}>
      <div style={headerBarStyle}>
        <h2 style={{ margin: 0, color: "#1e40af" }}>Liquidaciones Asociadas</h2>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <ResumenCompacto totals={totals} loading={loadingDetalles} />

          {/* Botón PDF */}
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={generatingPdf || nothingToExport || loadingDetalles}
            style={{
              ...pdfButtonStyle,
              opacity: generatingPdf || nothingToExport ? 0.6 : 1,
              cursor: generatingPdf || nothingToExport ? "not-allowed" : "pointer",
            }}
          >
            {generatingPdf ? (
              <><span style={miniSpinnerStyle} /> Generando...</>
            ) : (
              <><span style={{ fontSize: 16 }}>⬇</span> PDF</>
            )}
          </button>

          {/* Botón Excel */}
          <button
            type="button"
            onClick={handleDownloadXlsx}
            disabled={generatingXlsx || nothingToExport || loadingDetalles}
            style={{
              ...xlsxButtonStyle,
              opacity: generatingXlsx || nothingToExport ? 0.6 : 1,
              cursor: generatingXlsx || nothingToExport ? "not-allowed" : "pointer",
            }}
          >
            {generatingXlsx ? (
              <><span style={miniSpinnerGreenStyle} /> Generando...</>
            ) : (
              <><span style={{ fontSize: 16 }}>⬇</span> Excel</>
            )}
          </button>
        </div>
      </div>

      {nothingToExport ? (
        <p style={{ marginTop: 12 }}>No hay liquidaciones ni proveedores para esta orden.</p>
      ) : (
        <div style={{ position: "relative", overflowX: "auto" }}>
          {loadingDetalles && (
            <div style={overlayStyle}>
              <div style={bigSpinnerStyle} />
              <span style={{ marginLeft: 10, color: "#333", fontSize: 14 }}>
                Cargando liquidaciones...
              </span>
            </div>
          )}

          <table style={tableStyle}>
            <thead style={{ background: "#f1f5f9" }}>
              <tr>
                <th style={thStyle.id}>ID</th>
                <th style={thStyle.paciente}>Usuario</th>
                <th style={thStyle.inicio}>Inicio</th>
                <th style={thStyle.fin}>Fin</th>
                <th style={thStyle.factura}>Factura Nº</th>
                <th style={thStyle.tarifa}>Tarifa</th>
                <th style={thStyle.honorarios}>Honorarios</th>
                <th style={thStyle.estado}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {liquidaciones.map((liq, idx) => {
                const det = liqDetalles[liq.idLiquidacionClientes] || {};
                return (
                  <tr key={`liq-${liq.idLiquidacionClientes}`}
                    style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                    <td style={tdBase}>{liq.idLiquidacionClientes}</td>
                    <td style={tdBase}>
                      <a href={`/fichaliquidacion/${liq.idLiquidacionClientes}`}
                        style={{ color: "#1d4ed8", textDecoration: "none", fontWeight: 600, wordBreak: "break-word" }}>
                        {liq.NombrePaciente
                          ? `${liq.NombrePaciente} #${liq.idLiquidacionClientes}`
                          : `PacienteID:${liq.IdPaciente} #${liq.idLiquidacionClientes}`}
                      </a>
                    </td>
                    <td style={tdBase}>{liq.fechainicio || "-"}</td>
                    <td style={tdBase}>{liq.fechafin || "-"}</td>
                    <td style={tdBase}>
                      {liq.facturanumero && liq.facturanumero.trim() !== "" ? liq.facturanumero : "-"}
                    </td>
                    <td style={{ ...tdBase, textAlign: "right" }}>
                      {det.tarifa !== undefined ? formatMiles(det.tarifa) : "..."}
                    </td>
                    <td style={{ ...tdBase, textAlign: "right" }}>
                      {det.honorarios !== undefined ? formatMiles(det.honorarios) : "..."}
                    </td>
                    <td style={tdBase}>{liq.estado || "-"}</td>
                  </tr>
                );
              })}

              {proveedores.map((prov, idx) => (
                <tr key={`prov-${prov.idpagoproveedor}`}
                  style={{ background: idx % 2 === 0 ? "#ecfdf5" : "#d1fae5" }}>
                  <td style={tdBase}>{prov.idpagoproveedor}</td>
                  <td style={tdBase}>
                    <span style={{ color: "#047857", fontWeight: 600 }}>
                      {prov.proveedor} #{prov.idpagoproveedor}
                    </span>
                  </td>
                  <td style={tdBase}>{prov.fechaorden || "-"}</td>
                  <td style={tdBase}>-</td>
                  <td style={tdBase}>
                    {prov.factura && prov.factura.trim() !== "" ? prov.factura : "-"}
                  </td>
                  <td style={{ ...tdBase, textAlign: "right" }}>-</td>
                  <td style={{ ...tdBase, textAlign: "right" }}>
                    {prov.monto !== undefined ? formatMiles(prov.monto) : "..."}
                  </td>
                  <td style={tdBase}>{prov.estado || "-"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#eff6ff", fontWeight: 600, borderTop: "2px solid #2563eb" }}>
                <td colSpan={5} style={{ padding: "8px 10px", textAlign: "right" }}>Totales:</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{formatMiles(totals.tarifa)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{formatMiles(totals.honorarios)}</td>
                <td>—</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Estilos utilitarios ---------- */
const baseTh = (width, align = "left") => ({
  width, padding: "6px 10px", textAlign: align,
  borderBottom: "1px solid #e5e7eb", fontSize: 13,
  fontWeight: 600, color: "#374151",
});

const tdBase = {
  padding: "6px 10px", borderBottom: "1px solid #f1f5f9",
  fontSize: 13, color: "#1f2937", verticalAlign: "middle",
};

const headerBarStyle = {
  display: "flex", alignItems: "flex-start",
  justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 14,
};

const pdfButtonStyle = {
  padding: "8px 14px", background: "#2563eb", color: "#fff",
  border: "none", borderRadius: 6, fontSize: 14, fontWeight: 600,
  display: "flex", alignItems: "center", gap: 6,
  boxShadow: "0 1px 2px rgba(0,0,0,.08)",
};

const xlsxButtonStyle = {
  padding: "8px 14px", background: "#16a34a", color: "#fff",
  border: "none", borderRadius: 6, fontSize: 14, fontWeight: 600,
  display: "flex", alignItems: "center", gap: 6,
  boxShadow: "0 1px 2px rgba(0,0,0,.08)",
};

const miniSpinnerStyle = {
  width: 14, height: 14,
  border: "2px solid rgba(255,255,255,.35)",
  borderTop: "2px solid #fff", borderRight: "2px solid #fff",
  borderRadius: "50%", animation: "spin .8s linear infinite",
};

const miniSpinnerGreenStyle = { ...miniSpinnerStyle };

const bigSpinnerStyle = {
  width: 32, height: 32,
  border: "3px solid #ddd", borderTop: "3px solid #2563eb",
  borderRadius: "50%", animation: "spin 1s linear infinite",
};

const overlayStyle = {
  position: "absolute", inset: 0,
  background: "rgba(255,255,255,0.65)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5,
};

function ResumenCompacto({ totals, loading }) {
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", gap: 10, fontSize: 12,
      background: "#eff6ff", border: "1px solid #2563eb",
      padding: "6px 10px", borderRadius: 8, lineHeight: 1.3, color: "#1e40af",
    }}>
      <span>Registros: <strong>{totals.count}</strong></span>
      <span>Honorarios: <strong>{loading ? "..." : formatMiles(totals.honorarios)}</strong></span>
      <span>Tarifa: <strong>{loading ? "..." : formatMiles(totals.tarifa)}</strong></span>
    </div>
  );
}

/* ---------- Excel generation ---------- */
function generateXlsx({ liquidaciones, liqDetalles, proveedores, setGeneratingXlsx, orden, totals }) {
  setGeneratingXlsx(true);
  try {
    const wb = XLSX.utils.book_new();

    const HEADERS = ["ID", "Usuario", "Inicio", "Fin", "Factura Nº", "Tarifa", "Honorarios", "Estado"];

    // Filas de liquidaciones (valores numéricos para que Excel pueda sumar)
    const liqRows = liquidaciones.map((liq) => {
      const det = liqDetalles[liq.idLiquidacionClientes] || {};
      return [
        liq.idLiquidacionClientes,
        liq.NombrePaciente
          ? `${liq.NombrePaciente} #${liq.idLiquidacionClientes}`
          : `PacienteID:${liq.IdPaciente} #${liq.idLiquidacionClientes}`,
        liq.fechainicio || "-",
        liq.fechafin || "-",
        liq.facturanumero?.trim() || "-",
        det.tarifa !== undefined ? Number(det.tarifa) || 0 : "",
        det.honorarios !== undefined ? Number(det.honorarios) || 0 : "",
        liq.estado || "-",
      ];
    });

    // Filas de proveedores
    const provRows = proveedores.map((prov) => [
      prov.idpagoproveedor,
      `${prov.proveedor} #${prov.idpagoproveedor}`,
      prov.fechaorden || "-",
      "-",
      prov.factura?.trim() || "-",
      "-",
      prov.monto !== undefined ? Number(prov.monto) || 0 : "",
      prov.estado || "-",
    ]);

    // Fila de totales
    const totalRow = [
      "", "TOTALES", "", "", "",
      totals.tarifa,
      totals.honorarios,
      "",
    ];

    const wsData = [HEADERS, ...liqRows, ...provRows, totalRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Ancho de columnas
    ws["!cols"] = [
      { wch: 10 }, { wch: 40 }, { wch: 14 }, { wch: 14 },
      { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Liquidaciones");

    const safeInicio = (orden?.fechainicio || "").replace(/[/\s:]/g, "-");
    const safeFin = (orden?.fechafin || "").replace(/[/\s:]/g, "-");
    const fileName = `Resumen_ORDEN_ID_${orden?.idordendepago || "NA"}_${safeInicio}_${safeFin}.xlsx`;

    XLSX.writeFile(wb, fileName);
  } catch (e) {
    console.error("Error generando Excel:", e);
    alert("Error generando el Excel.");
  } finally {
    setGeneratingXlsx(false);
  }
}

/* ---------- PDF generation ---------- */
function generatePdf({ liquidaciones, liqDetalles, proveedores, setGeneratingPdf, orden }) {
  setGeneratingPdf(true);
  try {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4", compress: true });
    const marginX = 36;
    let cursorY = 40;

    const now = new Date();
    const fechaStr = now.toLocaleString();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(30);
    doc.text("Liquidaciones Asociadas", marginX, cursorY);
    cursorY += 16;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Generado: ${fechaStr}`, marginX, cursorY);
    cursorY += 10;

    const body = [
      ...liquidaciones.map((liq) => {
        const det = liqDetalles[liq.idLiquidacionClientes] || {};
        return [
          String(liq.idLiquidacionClientes),
          liq.NombrePaciente
            ? `${liq.NombrePaciente} #${liq.idLiquidacionClientes}`
            : liq.IdPaciente
              ? `PacienteID:${liq.IdPaciente} #${liq.idLiquidacionClientes}`
              : `#${liq.idLiquidacionClientes}`,
          liq.fechainicio || "-",
          liq.fechafin || "-",
          liq.facturanumero && liq.facturanumero.trim() !== "" ? liq.facturanumero : "-",
           det.tarifa !== undefined ? formatMiles(det.tarifa) : "-",
          det.honorarios !== undefined ? formatMiles(det.honorarios) : "-",
         
          liq.estado || "-",
        ];
      }),
      ...proveedores.map((prov) => [
        String(prov.idpagoproveedor),
        `${prov.proveedor} #${prov.idpagoproveedor}`,
        prov.fechaorden || "-",
        "-",
        prov.factura && prov.factura.trim() !== "" ? prov.factura : "-",
       
        "-",
         prov.monto !== undefined ? formatMiles(prov.monto) : "-",
        prov.estado || "-",
      ]),
    ];

    const totalHonorarios =
      liquidaciones.reduce((acc, liq) => {
        const det = liqDetalles[liq.idLiquidacionClientes];
        if (det && det.honorarios !== undefined) acc += Number(det.honorarios) || 0;
        return acc;
      }, 0) +
      proveedores.reduce((acc, prov) => acc + (Number(prov.monto) || 0), 0);

    const totalTarifa = liquidaciones.reduce((acc, liq) => {
      const det = liqDetalles[liq.idLiquidacionClientes];
      if (det && det.tarifa !== undefined) acc += Number(det.tarifa) || 0;
      return acc;
    }, 0);

    doc.autoTable({
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      head: [["ID", "Usuario", "Inicio", "Fin", "Factura Nº","Tarifa", "Honorarios", "Estado"]],
      body,
      styles: { fontSize: 8, cellPadding: 4, lineWidth: 0.1, overflow: "linebreak" },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 220 },
        2: { cellWidth: 70 },
        3: { cellWidth: 70 },
        4: { cellWidth: 70 },
        5: { cellWidth: 80, halign: "right" },
        6: { cellWidth: 80, halign: "right" },
        7: { cellWidth: 70 },
      },
      theme: "grid",
      didDrawPage: () => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(7);
        doc.setTextColor(120);
        doc.text(`Página ${doc.internal.getNumberOfPages()}`, pageWidth - marginX - 50, pageHeight - 12);
      },
    });

    let afterY = doc.lastAutoTable.finalY + 12;
    if (afterY > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      afterY = 40;
    }















    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30);
    doc.text("Totales", marginX, afterY);
    afterY += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const lines = [
      `Registros: ${liquidaciones.length + proveedores.length}`,
      `Honorarios: ${formatMiles(totalHonorarios)}`,
      `Tarifa: ${formatMiles(totalTarifa)}`,
    ];
    lines.forEach((l, i) => doc.text(l, marginX + 8, afterY + i * 12));


    const safeInicio = (orden?.fechainicio || "").replace(/[/\s:]/g, "-");
    const safeFin = (orden?.fechafin || "").replace(/[/\s:]/g, "-");
    const fileName = `Resumen_ORDEN_ID_${orden?.idordendepago || "NA"}_${safeInicio}_${safeFin}.pdf`;
    doc.save(fileName);

  } catch (e) {
    console.error("Error generando PDF liquidaciones:", e);
    alert("Error generando el PDF.");
  } finally {
    setGeneratingPdf(false);
  }
}
export default LiquidacionesAsociadas;